import React, { useContext, useMemo } from 'react';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import uuid from 'react-uuid';
import { ModalContext } from 'App';
import { ucwords, numberWithCommas } from 'helper';
import BusinessActivitiesUpdateModal from './BusinessActivitiesUpdateModal';

function BusinessActivities({
  data,
  businessInformation,
  isStartAssessment,
  checkAssessor,
  handleValidateQuarterlyBusinessActivities,
}) {
  const setModal = useContext(ModalContext);
  const transactionType = get(
    businessInformation,
    'attributes.transaction_type'
  );

  const handleUpdateBusinessActivities = (dataInfo) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <BusinessActivitiesUpdateModal
          updateList={dataInfo.attributes}
          businessActivity={dataInfo}
          businessInformation={businessInformation}
        />
      ),
      title: 'Update Business Activities',
      modalSize: 'modal-md',
    });
  };

  const list = useMemo(() => {
    const retirementActivities = get(
      businessInformation,
      'included.bplsRetirementActivities',
      []
    );

    switch (transactionType) {
      case 'RETIREMENT':
        return retirementActivities;
      default:
        return data;
    }
  }, [transactionType, businessInformation]);

  return (
    <div className="m-3">
      {list.length > 0
        ? list.map((businessActivity, index) => {
            const details = get(businessActivity, 'attributes.details') || {};
            const { quantity_matrix, quantity_matrix_details, ...detail } = details;

            return (
              <div key={businessActivity.id}>
                {['QUARTERLY'].indexOf(
                  get(businessInformation, 'attributes.transaction_type')
                ) > -1 &&
                  !get(businessActivity, 'attributes.is_validated') && (
                    <div className="text-right mx-2">
                      <button
                        type="button"
                        className="btn btn-primary cms-bg-color-primary cms-outline-primary font-weight-bold"
                        onClick={handleValidateQuarterlyBusinessActivities(
                          businessActivity
                        )}
                      >
                        Validate
                      </button>
                    </div>
                  )}
                <table className="transaction-table table table-borderless table-sm mb-1">
                  <tbody>
                    <tr className="d-flex">
                      <td>
                        <span className="title" style={{ fontSize: '16px' }}>
                          {`BUSINESS ACTIVITY ${index + 1}`}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-dark" width="250">
                        Business Line
                      </td>
                      <td className="text-dark" width="1">
                        :
                      </td>
                      <td colSpan={4}>
                        <b>{get(businessActivity, 'attributes.name') || '-'}</b>
                      </td>
                      {['RENEWAL'].indexOf(
                        get(businessInformation, 'attributes.transaction_type')
                      ) > -1 &&
                        get(
                          businessInformation,
                          'attributes.transaction_status'
                        ) === 'FOR CHECKING' &&
                        get(
                          businessInformation,
                          'included.bplsApplication.attributes.status'
                        ) === 'FOR LICENSE ASSESSMENT' &&
                        isStartAssessment === 1 &&
                        checkAssessor() && (
                          <td className="text-right">
                            <button
                              onClick={handleUpdateBusinessActivities(
                                businessActivity
                              )}
                              type="button"
                              className="btn-blank"
                            >
                              <i className="far fa-edit icon-edit" />
                            </button>
                          </td>
                        )}
                    </tr>
                    <tr>
                      <td className="text-dark" width="250">
                        Business Nature
                      </td>
                      <td className="text-dark" width="1">
                        :
                      </td>
                      <td colSpan={4}>
                        <b>
                          {get(
                            businessActivity,
                            'attributes.business_nature_name'
                          ) || '-'}
                        </b>
                      </td>
                    </tr>
                    {get(businessInformation, 'attributes.transaction_type') !==
                      'DELINQUENT' && (
                      <>
                        <tr>
                          <td className="text-dark" width="250">
                            Capital Amount
                          </td>
                          <td className="text-dark" width="1">
                            :
                          </td>
                          <td colSpan={4}>
                            <b>
                              {get(
                                businessActivity,
                                'attributes.capital_amount_human'
                              ) || '-'}
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-dark" width="250">
                            Gross Amount
                          </td>
                          <td className="text-dark" width="1">
                            :
                          </td>
                          <td colSpan={4}>
                            <b>
                              {get(
                                businessActivity,
                                'attributes.gross_amount_human'
                              ) || '-'}
                            </b>
                          </td>
                        </tr>
                      </>
                    )}
                    {Object.entries(detail).map((item) => {
                      const formatedData =
                        typeof item[1] === 'number'
                          ? parseFloat(item[1]).toFixed(2)
                          : ucwords((item[1] || '').replace(/_/g, ' '));
                      return (
                        <tr key={`bpls_business_detail${item[0]}`}>
                          <td className="text-dark" width="250">
                            {ucwords(item[0].replace(/_/g, ' '))}
                          </td>
                          <td className="text-dark" width="1">
                            :
                          </td>
                          <td colSpan={4}>
                            <b>{formatedData || '-'}</b>
                          </td>
                        </tr>
                      );
                    })}
                    {get(businessInformation, 'attributes.transaction_type') ===
                      'DELINQUENT' && (
                      <>
                        <tr>
                          <td className="text-dark" width="250">
                            Yearly Gross Amount
                          </td>
                        </tr>
                        {Object.entries(
                          get(
                            businessActivity,
                            'attributes.yearly_gross_amount'
                          )
                        ).map((x) => {
                          return (
                            <tr key={`yearly-gross-amount${x[0]}`}>
                              <td className="text-dark" width="250">
                                {x[0]}
                              </td>
                              <td className="text-dark" width="1">
                                :
                              </td>
                              <td colSpan={4}>
                                <b>{x[1]}</b>
                              </td>
                            </tr>
                          );
                        }) || '-'}
                      </>
                    )}
                  </tbody>
                </table>
                {!isEmpty(quantity_matrix) ? (
                  Object.entries(quantity_matrix).map(([label, quantityMatrixData]) => {

                    return (
                      <React.Fragment>
                        <div key={uuid()} className='text-dark text-capitalize mx-2' style={{ fontSize: '0.875rem'}}>
                          {label || ''}
                        </div>
                        <div>
                          <div className='w-100 border rounded-lg m-2'>
                            <table className="table table-borderless table-sm table-striped table-hover mb-0">
                              <thead>
                                <tr className='form-group'>
                                  <td className='col-4 text-center form-label border-right'>Unit</td>
                                  <td className='col-4 text-center form-label border-right'>Capacity</td>
                                  <td className='col-4 text-center form-label'>Number of Units</td>
                                </tr>
                              </thead>
                              <tbody>
                                {quantityMatrixData.map(item => {
                                    const multiplier = get(item, 'multiplier');

                                    return (
                                      <tr key={multiplier} className="top-border form-group">
                                        <td className="text-left form-label border-right">
                                          {get(item, 'unit')} 
                                        </td>
                                        <td className="text-left form-label border-right">
                                          {get(item, 'capacity')}
                                        </td>
                                        <td className="text-left form-label">
                                          {numberWithCommas(get(item, 'value', 0))}
                                        </td>
                                      </tr>
                                    )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </React.Fragment>
                    )
                  })
                ) : ''}
                <hr />
              </div>
            );
          })
        : null}
    </div>
  );
}

BusinessActivities.defaultProps = {
  data: {},
  isStartAssessment: 0,
};

BusinessActivities.propTypes = {
  data: PropTypes.instanceOf(Object),
  businessInformation: PropTypes.instanceOf(Object).isRequired,
  checkAssessor: PropTypes.instanceOf(Function).isRequired,
  isStartAssessment: PropTypes.number,
  handleValidateQuarterlyBusinessActivities:
    PropTypes.instanceOf(Function).isRequired,
};

export default BusinessActivities;
